import React, { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAppointmentCharges } from '../../api/appointments';
import ModalPortal from '../shared/ModalPortal';
import { CancelIcon } from '../../svgs/CancelIcon';
import { Appointment } from '../../types/appointment';
import Button from '../shared/Button';
import html2canvas from 'html2canvas';
import { TempleHSLogo } from "../../svgs/TempleHSLogo"

interface ChargesModalProps {
  isVisible: boolean;
  onClose: () => void;
  appointmentId: string;
  appointmentInfo: Appointment | null;
}

export const ChargesModal: React.FC<ChargesModalProps> = ({
  isVisible,
  onClose,
  appointmentId,
  appointmentInfo,
}) => {
  const { data: charges, isLoading, error } = useQuery({
    queryKey: ['appointmentCharges', appointmentId],
    queryFn: () => getAppointmentCharges(appointmentId),
    enabled: isVisible && !!appointmentId,
  });

  const modalContentRef = useRef<HTMLDivElement>(null);

  const downloadInvoice = async () => {
    if (!modalContentRef.current || !appointmentInfo?.appointmentDate) return;

    // Show the logo and hide the close button for the screenshot
    const closeButton = modalContentRef.current.querySelector('.close-button');
    const logoElement = modalContentRef.current.querySelector('.invoice-logo');
    const downloadButton = modalContentRef.current.querySelector('.download-button');

    if (closeButton) closeButton.classList.add('hidden');
    if (logoElement) logoElement.classList.remove('hidden');
    if (downloadButton) downloadButton.classList.add('hidden');

    try {
      const canvas = await html2canvas(modalContentRef.current, {
        scale: 2, // Higher scale for better quality
        useCORS: true,
        logging: false,
        backgroundColor: '#f9fafb', // Match the modal background
      });
      const data = canvas.toDataURL('image/png');
      const link = document.createElement('a');

      // Show the download button again after capture
      if (downloadButton) {
        (downloadButton as HTMLElement).style.display = '';
      }

      link.href = data;
      link.download = `templehs-${appointmentInfo.appointmentDate.split('T')[0]}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Reset the UI
      if (closeButton) closeButton.classList.remove('hidden');
      if (logoElement) logoElement.classList.add('hidden');
      if (downloadButton) downloadButton.classList.remove('hidden');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      // Ensure UI is reset even if there's an error
      if (closeButton) closeButton.classList.remove('hidden');
      if (logoElement) logoElement.classList.add('hidden');
      if (downloadButton) downloadButton.classList.remove('hidden');
    }
  };

  if (!isVisible) return null;

  const formatCurrency = (amount: number | undefined) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[999] cursor-pointer flex items-center justify-center bg-black bg-opacity-20"
        onClick={onClose}
      >
        <div
          ref={modalContentRef}
          className="bg-gray-50 cursor-default rounded-lg px-8 py-14 w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className='flex justify-between items-center mb-4'>
            <div className='flex flex-col'>
              <small className='text-[10px] font-semibold text-gray-400'>{appointmentInfo?.appointmentDate}</small>
              <small className='text-xs text-gray-500'>Patient: {appointmentInfo?.patientName}</small>
            </div>
            <div className="relative">
              <button
                onClick={onClose}
                className="close-button text-gray-400 hover:text-gray-500"
              >
                <CancelIcon className="h-6 w-6" />
              </button>
              <div className="invoice-logo hidden absolute right-0">
                <TempleHSLogo width={96} height={38} className="h-8 w-auto" />
              </div>
            </div>
          </div>
          <div className=" mb-4">
            <h3 className="text-3xl font-bold text-gray-700">
              Invoice
            </h3>

          </div>

          {isLoading ? (
            <div className="text-center py-4">Loading charges...</div>
          ) : error ? (
            <div className="text-red-500 text-center py-4">
              Error loading charges. Please try again.
            </div>
          ) : (
            <div className="space-y-5 mt-8">
              <div className="flex justify-between">
                <span className='font-medium text-gray-500'>Meeting Cost:</span>
                <span className='font-bold text-gray-500'>{formatCurrency(charges?.meetingCost || appointmentInfo?.meetingCost)}</span>
              </div>
              <div className="flex justify-between">
                <span className='font-medium text-gray-500'>Recording Cost:</span>
                <span className='font-bold text-gray-500'>{formatCurrency(charges?.recordingCost || appointmentInfo?.recordingCost)}</span>
              </div>
              <div className="flex justify-between">
                <span className='font-medium text-gray-500'>AI Documentation Cost:</span>
                <span className='font-bold text-gray-500'>{formatCurrency(charges?.transcriptionCost || appointmentInfo?.transcriptionCost)}</span>
              </div>
              <div className="flex justify-between">
                <span className='font-medium text-gray-500'>Media Concatenation Cost:</span>
                <span className='font-bold text-gray-500'>{formatCurrency(charges?.concatenationCost || appointmentInfo?.concatenationCost)}</span>
              </div>
              <hr />
              <div className="flex justify-end font-semibold text-lg">
                <span className='font-bold text-3xl text-gray-500'>{formatCurrency(charges?.totalCost || appointmentInfo?.totalCost)}</span>
              </div>
            </div>
          )}

          <p className='pt-3 text-xs text-gray-500'>This invoice reflects the costs for the current appointment. Total monthly charges will be calculated and billed at the end of the billing cycle.</p>
          <div className="mt-6 flex justify-end download-button">
            <Button
              onClick={downloadInvoice}
              variant="secondary"
              label="Download Invoice"
            />
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
